import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Card,
  InputBase,
  Divider,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  getUsersList,
  getDepartments,
  createAodmUser,
  deleteAodmUser,
  updateAodmUser,
} from "../../utils/api";
import useAlerts from "src/components/Alerts";
import DeleteDialog from "src/components/DeleteDialog";

const MIN_PASSWORD_LENGTH = 6;

interface FormValues {
  name: string;
  email: string;
  username: string;
  password: string;
  password_confirm: string;
  dept_ids: string;
  role: string;
}

const initialFormValues: FormValues = {
  name: "",
  email: "",
  username: "",
  password: "",
  password_confirm: "",
  dept_ids: "",
  role: "",
};

const mapUserRoleToForm = (user: any): string => {
  if (user?.role >= 5 || user?.roleType === "AODM Admin" || user?.roleType === "aodm_admin") {
    return "aodm_admin";
  }
  return "aodm";
};

const PasswordField: React.FC<{
  label: string;
  name: "password" | "password_confirm";
  value: string;
  required?: boolean;
  helperText?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  show: boolean;
  onToggle: () => void;
}> = ({ label, name, value, required, helperText, onChange, show, onToggle }) => (
  <TextField
    label={label}
    name={name}
    fullWidth
    required={required}
    margin="dense"
    type={show ? "text" : "password"}
    InputLabelProps={{ shrink: true }}
    placeholder={label}
    value={value}
    onChange={onChange}
    helperText={helperText}
    autoComplete="new-password"
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label={show ? "Hide password" : "Show password"}
            onClick={onToggle}
            edge="end"
            size="small"
          >
            <Icon icon={show ? "mdi:eye-off" : "mdi:eye"} width={22} />
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

const Users: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showAlert } = useAlerts();

  const [usersList, setUsersList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const resetPasswordVisibility = () => {
    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  const handleBackButton = () => {
    navigate("/configuration");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const fetchUsers = async () => {
    try {
      const users = await getUsersList(new Date().getTime());
      setUsersList(users);
    } catch (error) {
      console.error("Error fetching users:", error);
      showAlert("Failed to load users", "error");
    }
  };

  const fetchDepartments = async () => {
    try {
      const departments = await getDepartments();
      setDepartmentList(departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      showAlert("Failed to load departments", "error");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validatePasswordPair = (required: boolean): boolean => {
    const { password, password_confirm } = formValues;
    const hasAny = Boolean(password || password_confirm);

    if (required || hasAny) {
      if (!password || !password_confirm) {
        showAlert("Please enter and confirm the password.", "warning");
        return false;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        showAlert(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`, "warning");
        return false;
      }
      if (password !== password_confirm) {
        showAlert("Passwords do not match. Please try again.", "warning");
        return false;
      }
    }
    return true;
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setFormValues(initialFormValues);
    resetPasswordVisibility();
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditingUserId(null);
    setFormValues(initialFormValues);
    resetPasswordVisibility();
  };

  const openCreateDialog = () => {
    setFormValues(initialFormValues);
    resetPasswordVisibility();
    setCreateOpen(true);
  };

  const openEditDialog = (user: any) => {
    setEditingUserId(user.id);
    setFormValues({
      name: (user.name || `${user.fname || ""} ${user.lname || ""}`).trim(),
      email: user.email || "",
      username: user.username || "",
      password: "",
      password_confirm: "",
      dept_ids: user.departments?.[0]?.id?.toString() || "",
      role: mapUserRoleToForm(user),
    });
    resetPasswordVisibility();
    setEditOpen(true);
  };

  const handleCreate = async () => {
    if (
      !formValues.name ||
      !formValues.email ||
      !formValues.username ||
      !formValues.dept_ids ||
      !formValues.role
    ) {
      showAlert("Please fill all fields correctly.", "warning");
      return;
    }
    if (!validatePasswordPair(true)) return;

    setSaving(true);
    try {
      const { password_confirm, ...payload } = formValues;
      const newUser = await createAodmUser(payload);
      if (newUser) {
        showAlert(`User ${newUser?.user?.fname ?? ""} created successfully`, "success");
        closeCreateDialog();
        fetchUsers();
      } else {
        showAlert("User creation failed!", "error");
      }
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors)) {
        if (apiErrors.includes("The email has already been taken.")) {
          showAlert("Email already exists. Please use a different email.", "warning");
          return;
        }
        if (apiErrors.includes("The username has already been taken.")) {
          showAlert("Username already exists. Please use a different username.", "warning");
          return;
        }
        showAlert(apiErrors[0] || "User creation failed!", "error");
        return;
      }
      console.error("Error creating user:", error);
      showAlert("User creation failed!", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (editingUserId === null) return;

    if (
      !formValues.name ||
      !formValues.email ||
      !formValues.username ||
      !formValues.dept_ids ||
      !formValues.role
    ) {
      showAlert("Please fill all fields correctly.", "warning");
      return;
    }
    if (!validatePasswordPair(false)) return;

    setSaving(true);
    try {
      const payload: Record<string, string | number> = {
        user_id: editingUserId,
        name: formValues.name,
        email: formValues.email,
        username: formValues.username,
        role: formValues.role,
        dept_ids: formValues.dept_ids,
      };

      if (formValues.password) {
        payload.password = formValues.password;
      }

      await updateAodmUser(payload);
      showAlert(
        formValues.password
          ? "User updated and password set successfully"
          : "User updated successfully",
        "success"
      );
      closeEditDialog();
      fetchUsers();
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length) {
        showAlert(apiErrors[0], "error");
        return;
      }
      if (apiErrors && typeof apiErrors === "object") {
        const first = Object.values(apiErrors).flat()[0];
        showAlert(String(first || "Failed to update user"), "error");
        return;
      }
      console.error("Error updating user:", error);
      showAlert("Failed to update user", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (userToDelete === null) return;
    try {
      await deleteAodmUser(userToDelete);
      showAlert("User deleted successfully", "success");
      setUsersList(usersList.filter((user) => user.id !== userToDelete));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      showAlert("Failed to delete user", "error");
    }
  };

  const filteredData = usersList.filter((row: any) =>
    Object.values(row).some(
      (value: any) =>
        value?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.id?.toString().includes(searchQuery)
    )
  );

  const renderUserFormFields = (mode: "create" | "edit") => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                required
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="Enter Name"
                value={formValues.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email ID"
                name="email"
                fullWidth
                required
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="Enter email ID"
                value={formValues.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Username"
                name="username"
                fullWidth
                required
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="Enter username"
                value={formValues.username}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordField
                label={mode === "edit" ? "New Password" : "Password"}
                name="password"
                value={formValues.password}
                required={mode === "create"}
                helperText={
                  mode === "edit"
                    ? "Leave blank to keep the current password"
                    : `Minimum ${MIN_PASSWORD_LENGTH} characters`
                }
                onChange={handleChange}
                show={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordField
                label={mode === "edit" ? "Confirm New Password" : "Confirm Password"}
                name="password_confirm"
                value={formValues.password_confirm}
                required={mode === "create"}
                onChange={handleChange}
                show={showPasswordConfirm}
                onToggle={() => setShowPasswordConfirm((v) => !v)}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, p: 2 }}>
          <Box
            sx={{
              border: "1px dashed #6c8ebf",
              borderRadius: "8px",
              padding: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Assign Department
            </Typography>
            <TextField
              select
              label="Department *"
              name="dept_ids"
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              value={formValues.dept_ids}
              onChange={handleChange}
            >
              {departmentList?.map((dept: any) => (
                <MenuItem key={dept.id} value={dept.id?.toString()}>
                  {dept.dept_name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box
            sx={{
              border: "1px dashed #6c8ebf",
              borderRadius: "8px",
              padding: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Assign Role
            </Typography>
            <TextField
              select
              name="role"
              label="Role *"
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              value={formValues.role}
              onChange={handleChange}
            >
              <MenuItem value="aodm">AODM</MenuItem>
              <MenuItem value="aodm_admin">AODM Admin</MenuItem>
            </TextField>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ padding: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            marginLeft: "70px",
          }}
          onClick={handleBackButton}
        >
          <Icon
            icon="mdi:arrow-left"
            style={{
              fontSize: 74,
              color: theme.palette.obs.main,
              marginRight: 8,
            }}
          />
          <Typography variant="h6" sx={{ color: theme.palette.obs.main }}>
            Configuration Home Page
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon icon="mdi:plus-circle-outline" />}
          sx={{
            backgroundColor: theme.palette.obs.main,
            color: "#fff",
            borderRadius: "8px",
            textTransform: "none",
          }}
          onClick={openCreateDialog}
        >
          Add User
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        style={{
          maxWidth: "88%",
          margin: "auto",
          padding: "0 20px",
          marginLeft: "80px",
          marginTop: "40px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography variant="h6" color={theme.palette.obs.main}>
            <b> No. of Users: {usersList?.length ?? 0}</b>
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "2px 10px",
              width: "300px",
            }}
          >
            <Icon icon="mdi:search" />
            <InputBase
              placeholder="Search users"
              inputProps={{ "aria-label": "search" }}
              sx={{ ml: 1, width: "100%" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
        </Box>
        <Divider />
        <Table>
          <TableHead>
            <TableRow>
              {["USER ID", "NAME", "USERNAME", "EMAIL ID", "ROLE", "DEPARTMENT", ""].map(
                (header) => (
                  <TableCell
                    key={header || "actions"}
                    sx={{ fontWeight: "bold", color: theme.palette.obs.main }}
                  >
                    {header}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.map((user: any) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.roleType}</TableCell>
                <TableCell>
                  {user?.departments?.map((dept: any) => dept?.dept_name ?? "N.A").join(", ")}
                </TableCell>
                <TableCell sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <IconButton onClick={() => openEditDialog(user)} aria-label="Edit user">
                    <Icon icon="mdi:pencil" style={{ fontSize: 24, color: "#5a6e8c" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setUserToDelete(user.id);
                      setDeleteDialogOpen(true);
                    }}
                    aria-label="Delete user"
                  >
                    <Icon icon="mdi:delete" style={{ fontSize: 24, color: "#e53935" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        title="Confirm Deletion"
        description="Are you sure you want to delete this User?"
      />

      {/* Create User */}
      <Dialog
        open={createOpen}
        onClose={closeCreateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 20 } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Create User
          </Typography>
          <IconButton onClick={closeCreateDialog}>
            <Icon icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {renderUserFormFields("create")}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                backgroundColor: "#6c8ebf",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                padding: "16px 32px",
                fontSize: "18px",
              }}
              onClick={handleCreate}
            >
              CREATE USER
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit User */}
      <Dialog
        open={editOpen}
        onClose={closeEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 20 } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Edit User
          </Typography>
          <IconButton onClick={closeEditDialog}>
            <Icon icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {renderUserFormFields("edit")}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              disabled={saving}
              onClick={closeEditDialog}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                padding: "12px 28px",
                fontSize: "16px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                backgroundColor: "#6c8ebf",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                padding: "12px 28px",
                fontSize: "16px",
              }}
              onClick={handleUpdate}
            >
              SAVE CHANGES
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Users;
